/**
 * 📢 BROADCAST MESSAGING PAGE
 * Manage message templates and campaigns
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Paper,
  Divider,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Send,
  Email,
  Sms,
  Visibility,
  VisibilityOff,
  BarChart,
  ContentCopy,
  Campaign as CampaignIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import {
  broadcastService,
  type MessageTemplate,
  type BroadcastCampaign,
  type CreateTemplateData,
  type UpdateTemplateData,
  type SendCampaignData,
  type TemplateType,
  type TemplateCategory,
  type CampaignType,
  type TargetAudience,
} from '@/services/broadcastService';
import toast from 'react-hot-toast';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`broadcast-tabpanel-${index}`}
      aria-labelledby={`broadcast-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const BroadcastMessagingPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);

  // Templates state
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateDialog, setTemplateDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    template: MessageTemplate | null;
  }>({ open: false, mode: 'create', template: null });
  const [deleteTemplateDialog, setDeleteTemplateDialog] = useState<{
    open: boolean;
    template: MessageTemplate | null;
  }>({ open: false, template: null });

  // Campaigns state
  const [campaigns, setCampaigns] = useState<BroadcastCampaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [campaignStatsDialog, setCampaignStatsDialog] = useState<{
    open: boolean;
    campaignId: string | null;
  }>({ open: false, campaignId: null });

  // Form state
  const [templateForm, setTemplateForm] = useState<CreateTemplateData>({
    name: '',
    templateType: 'both',
    category: 'transactional',
    smsMessage: '',
    emailSubject: '',
    emailBodyHtml: '',
    variables: [],
    isActive: true,
  });

  const [campaignForm, setCampaignForm] = useState<SendCampaignData>({
    name: '',
    campaignType: 'both',
    targetAudience: 'all_users',
    templateVariables: {},
  });

  // Separate state for phone numbers and emails when targeting specific users
  const [targetPhones, setTargetPhones] = useState<string>('');
  const [targetEmails, setTargetEmails] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadTemplates();
    loadCampaigns();
  }, []);

  // ========== TEMPLATE FUNCTIONS ==========

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const result = await broadcastService.getTemplates();
      if (result.success && result.data) {
        setTemplates(result.data.templates || []);
      } else {
        toast.error(result.error || 'Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleOpenTemplateDialog = (template?: MessageTemplate) => {
    if (template) {
      setTemplateDialog({ open: true, mode: 'edit', template });
      setTemplateForm({
        name: template.name,
        templateType: template.templateType,
        category: template.category,
        smsMessage: template.smsMessage || '',
        emailSubject: template.emailSubject || '',
        emailBodyHtml: template.emailBodyHtml || '',
        variables: template.variables,
        isActive: template.isActive,
      });
    } else {
      setTemplateDialog({ open: true, mode: 'create', template: null });
      setTemplateForm({
        name: '',
        templateType: 'both',
        category: 'transactional',
        smsMessage: '',
        emailSubject: '',
        emailBodyHtml: '',
        variables: [],
        isActive: true,
      });
    }
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialog({ open: false, mode: 'create', template: null });
  };

  const handleSubmitTemplate = async () => {
    try {
      setSubmitting(true);

      // Validation
      if (!templateForm.name) {
        toast.error('Template name is required');
        return;
      }

      if (templateForm.templateType === 'sms' || templateForm.templateType === 'both') {
        if (!templateForm.smsMessage || templateForm.smsMessage.trim() === '') {
          toast.error('SMS message is required for SMS templates');
          return;
        }
      }

      if (templateForm.templateType === 'email' || templateForm.templateType === 'both') {
        if (!templateForm.emailSubject || templateForm.emailSubject.trim() === '') {
          toast.error('Email subject is required for email templates');
          return;
        }
        if (!templateForm.emailBodyHtml || templateForm.emailBodyHtml.trim() === '') {
          toast.error('Email body is required for email templates');
          return;
        }
      }

      const result = templateDialog.mode === 'edit' && templateDialog.template
        ? await broadcastService.updateTemplate(templateDialog.template.id, templateForm)
        : await broadcastService.createTemplate(templateForm);

      if (result.success) {
        toast.success(templateDialog.mode === 'edit' ? 'Template updated' : 'Template created');
        handleCloseTemplateDialog();
        loadTemplates();
      } else {
        toast.error(result.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleTemplateActive = async (template: MessageTemplate) => {
    try {
      const result = await broadcastService.updateTemplate(template.id, {
        isActive: !template.isActive
      });

      if (result.success) {
        toast.success(`Template ${!template.isActive ? 'activated' : 'deactivated'}`);
        loadTemplates();
      } else {
        toast.error(result.error || 'Failed to update template');
      }
    } catch (error) {
      console.error('Error toggling template:', error);
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTemplateDialog.template) return;

    try {
      setSubmitting(true);
      const result = await broadcastService.deleteTemplate(deleteTemplateDialog.template.id);

      if (result.success) {
        toast.success('Template deleted');
        setDeleteTemplateDialog({ open: false, template: null });
        loadTemplates();
      } else {
        toast.error(result.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setSubmitting(false);
    }
  };

  const extractVariables = (text: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = text.matchAll(regex);
    return [...new Set([...matches].map(match => match[1]))];
  };

  const handleTemplateTextChange = (field: 'smsMessage' | 'emailSubject' | 'emailBodyHtml', value: string) => {
    setTemplateForm(prev => {
      const newForm = { ...prev, [field]: value };

      // Auto-extract variables
      const allText = `${newForm.smsMessage} ${newForm.emailSubject} ${newForm.emailBodyHtml}`;
      const variables = extractVariables(allText);

      return { ...newForm, variables };
    });
  };

  // ========== CAMPAIGN FUNCTIONS ==========

  const loadCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      const result = await broadcastService.getCampaigns();
      if (result.success && result.data) {
        setCampaigns(result.data.campaigns || []);
      } else {
        toast.error(result.error || 'Failed to load campaigns');
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleOpenCampaignDialog = () => {
    setCampaignDialog(true);
    setCampaignForm({
      name: '',
      campaignType: 'both',
      targetAudience: 'all_users',
      templateVariables: {},
    });
    setTargetPhones('');
    setTargetEmails('');
  };

  const handleCloseCampaignDialog = () => {
    setCampaignDialog(false);
  };

  const handleSendCampaign = async () => {
    try {
      setSubmitting(true);

      if (!campaignForm.name) {
        toast.error('Campaign name is required');
        return;
      }

      if (!campaignForm.templateId && !campaignForm.customMessage) {
        toast.error('Please select a template or provide a custom message');
        return;
      }

      // Prepare campaign data with separate phone/email fields
      const campaignData: any = {
        name: campaignForm.name,
        campaignType: campaignForm.campaignType,
        targetAudience: campaignForm.targetAudience,
        templateId: campaignForm.templateId,
        customMessage: campaignForm.customMessage,
        templateVariables: campaignForm.templateVariables,
        segmentFilters: campaignForm.segmentFilters,
        scheduledAt: campaignForm.scheduledAt,
      };

      // Add targeting fields based on audience type
      if (campaignForm.targetAudience === 'specific_users') {
        const phones = targetPhones.split('\n').map(p => p.trim()).filter(Boolean);
        const emails = targetEmails.split('\n').map(e => e.trim()).filter(Boolean);

        if (phones.length === 0 && emails.length === 0) {
          toast.error('Please enter at least one phone number or email address');
          return;
        }

        // Send phones and emails in separate fields
        if (phones.length > 0) {
          campaignData.targetPhones = phones;
        }
        if (emails.length > 0) {
          campaignData.targetEmails = emails;
        }
      }

      const result = await broadcastService.sendCampaign(campaignData);

      if (result.success) {
        toast.success('Campaign sent successfully');
        handleCloseCampaignDialog();
        loadCampaigns();
      } else {
        toast.error(result.error || 'Failed to send campaign');
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error('Failed to send campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const getTemplateTypeIcon = (type: TemplateType) => {
    switch (type) {
      case 'sms': return <Sms fontSize="small" />;
      case 'email': return <Email fontSize="small" />;
      case 'both': return <><Sms fontSize="small" sx={{ mr: 0.5 }} /><Email fontSize="small" /></>;
    }
  };

  const getCategoryColor = (category: TemplateCategory): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    switch (category) {
      case 'marketing': return 'primary';
      case 'transactional': return 'success';
      case 'notification': return 'info';
      case 'promotional': return 'warning';
      default: return 'default';
    }
  };

  const getCampaignStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'sent': return 'success';
      case 'sending': return 'primary';
      case 'scheduled': return 'info';
      case 'failed': return 'error';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Broadcast Messaging
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage message templates and send campaigns to users
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
          <Tab label="Templates" icon={<MessageIcon />} iconPosition="start" />
          <Tab label="Campaigns" icon={<CampaignIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Templates Tab */}
      <TabPanel value={currentTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">Message Templates</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenTemplateDialog()}
          >
            Create Template
          </Button>
        </Box>

        <Card>
          <CardContent>
            {loadingTemplates ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : templates.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <MessageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No templates yet
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                  Create your first message template
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenTemplateDialog()}>
                  Create Template
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Variables</TableCell>
                      <TableCell align="center">Usage</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {template.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(template.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getTemplateTypeIcon(template.templateType)}
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                              {template.templateType}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={template.category}
                            size="small"
                            color={getCategoryColor(template.category)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {template.variables.slice(0, 3).map((variable) => (
                              <Chip
                                key={variable}
                                label={`{{${variable}}}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                              />
                            ))}
                            {template.variables.length > 3 && (
                              <Chip
                                label={`+${template.variables.length - 3}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={template.usageCount}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={template.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={template.isActive ? 'success' : 'default'}
                            icon={template.isActive ? <Visibility /> : <VisibilityOff />}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title={template.isActive ? 'Deactivate' : 'Activate'}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleTemplateActive(template)}
                                sx={{
                                  color: template.isActive ? 'warning.main' : 'success.main',
                                }}
                              >
                                {template.isActive ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenTemplateDialog(template)}
                                sx={{ color: 'primary.main' }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteTemplateDialog({ open: true, template })}
                                sx={{ color: 'error.main' }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Campaigns Tab */}
      <TabPanel value={currentTab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">Broadcast Campaigns</Typography>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={handleOpenCampaignDialog}
          >
            New Campaign
          </Button>
        </Box>

        <Card>
          <CardContent>
            {loadingCampaigns ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : campaigns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CampaignIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No campaigns yet
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                  Create your first broadcast campaign
                </Typography>
                <Button variant="contained" startIcon={<Send />} onClick={handleOpenCampaignDialog}>
                  New Campaign
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell align="center">Recipients</TableCell>
                      <TableCell align="center">Delivery</TableCell>
                      <TableCell align="center">Engagement</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {campaign.name}
                          </Typography>
                          {campaign.template && (
                            <Typography variant="caption" color="text.secondary">
                              Template: {campaign.template.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getTemplateTypeIcon(campaign.campaignType)}
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                              {campaign.campaignType}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.targetAudience.replace('_', ' ')}
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {campaign.totalRecipients.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {campaign.deliveredCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {campaign.totalRecipients > 0
                                ? `${Math.round((campaign.deliveredCount / campaign.totalRecipients) * 100)}%`
                                : '0%'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {campaign.campaignType !== 'sms' && (
                            <Box>
                              <Typography variant="caption" display="block">
                                Opens: {campaign.openedCount || 0}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Clicks: {campaign.clickedCount || 0}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.status}
                            size="small"
                            color={getCampaignStatusColor(campaign.status)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {campaign.sentAt
                              ? new Date(campaign.sentAt).toLocaleDateString()
                              : new Date(campaign.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Analytics">
                            <IconButton
                              size="small"
                              onClick={() => setCampaignStatsDialog({ open: true, campaignId: campaign.id })}
                              sx={{ color: 'primary.main' }}
                            >
                              <BarChart />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Template Dialog */}
      <Dialog open={templateDialog.open} onClose={handleCloseTemplateDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {templateDialog.mode === 'edit' ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Template Name"
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              required
              fullWidth
              placeholder="e.g., Booking Confirmation"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Template Type</InputLabel>
                  <Select
                    value={templateForm.templateType}
                    label="Template Type"
                    onChange={(e) => setTemplateForm({ ...templateForm, templateType: e.target.value as TemplateType })}
                  >
                    <MenuItem value="sms">SMS Only</MenuItem>
                    <MenuItem value="email">Email Only</MenuItem>
                    <MenuItem value="both">Both SMS & Email</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={templateForm.category}
                    label="Category"
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value as TemplateCategory })}
                  >
                    <MenuItem value="transactional">Transactional</MenuItem>
                    <MenuItem value="marketing">Marketing</MenuItem>
                    <MenuItem value="notification">Notification</MenuItem>
                    <MenuItem value="promotional">Promotional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {(templateForm.templateType === 'sms' || templateForm.templateType === 'both') && (
              <Box>
                <TextField
                  label="SMS Message"
                  value={templateForm.smsMessage}
                  onChange={(e) => handleTemplateTextChange('smsMessage', e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                  required
                  placeholder="Hi {{firstName}}! Your booking {{bookingNumber}} is confirmed for {{startDate}}."
                  helperText="Use {{variableName}} for dynamic content. SMS messages are limited to 160 characters per segment."
                  error={!templateForm.smsMessage && (templateForm.templateType === 'sms' || templateForm.templateType === 'both')}
                />
                <Typography variant="caption" color="text.secondary">
                  Length: {templateForm.smsMessage?.length || 0} characters
                </Typography>
              </Box>
            )}

            {(templateForm.templateType === 'email' || templateForm.templateType === 'both') && (
              <>
                <TextField
                  label="Email Subject"
                  value={templateForm.emailSubject}
                  onChange={(e) => handleTemplateTextChange('emailSubject', e.target.value)}
                  fullWidth
                  required
                  placeholder="Booking Confirmed - {{bookingNumber}}"
                  helperText="Use {{variableName}} for dynamic content"
                  error={!templateForm.emailSubject && (templateForm.templateType === 'email' || templateForm.templateType === 'both')}
                />

                <TextField
                  label="Email Body (HTML)"
                  value={templateForm.emailBodyHtml}
                  onChange={(e) => handleTemplateTextChange('emailBodyHtml', e.target.value)}
                  multiline
                  rows={8}
                  fullWidth
                  required
                  placeholder="<h1>Booking Confirmed!</h1><p>Hi {{firstName}},</p><p>Your booking {{bookingNumber}} is confirmed.</p>"
                  helperText="Use HTML for formatting. Use {{variableName}} for dynamic content."
                  sx={{ fontFamily: 'monospace' }}
                  error={!templateForm.emailBodyHtml && (templateForm.templateType === 'email' || templateForm.templateType === 'both')}
                />
              </>
            )}

            {templateForm.variables.length > 0 && (
              <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom>
                  Detected Variables:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {templateForm.variables.map((variable) => (
                    <Chip
                      key={variable}
                      label={`{{${variable}}}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  ))}
                </Box>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={templateForm.isActive}
                  onChange={(e) => setTemplateForm({ ...templateForm, isActive: e.target.checked })}
                />
              }
              label="Active (available for use in campaigns)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitTemplate}
            variant="contained"
            disabled={
              submitting ||
              !templateForm.name ||
              ((templateForm.templateType === 'sms' || templateForm.templateType === 'both') && !templateForm.smsMessage) ||
              ((templateForm.templateType === 'email' || templateForm.templateType === 'both') && (!templateForm.emailSubject || !templateForm.emailBodyHtml))
            }
          >
            {submitting ? <CircularProgress size={20} /> : templateDialog.mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Template Dialog */}
      <Dialog open={deleteTemplateDialog.open} onClose={() => setDeleteTemplateDialog({ open: false, template: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{deleteTemplateDialog.template?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTemplateDialog({ open: false, template: null })} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTemplate} variant="contained" color="error" disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Dialog */}
      <Dialog open={campaignDialog} onClose={handleCloseCampaignDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create Broadcast Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Campaign Name"
              value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              required
              fullWidth
              placeholder="e.g., New Year Welcome Campaign"
            />

            <FormControl fullWidth>
              <InputLabel>Campaign Type</InputLabel>
              <Select
                value={campaignForm.campaignType}
                label="Campaign Type"
                onChange={(e) => setCampaignForm({ ...campaignForm, campaignType: e.target.value as CampaignType })}
              >
                <MenuItem value="sms">SMS Only</MenuItem>
                <MenuItem value="email">Email Only</MenuItem>
                <MenuItem value="both">Both SMS & Email</MenuItem>
              </Select>
            </FormControl>

            <Divider><Typography variant="caption">Message Source</Typography></Divider>

            <FormControl fullWidth>
              <InputLabel>Select Template (Optional)</InputLabel>
              <Select
                value={campaignForm.templateId || ''}
                label="Select Template (Optional)"
                onChange={(e) => setCampaignForm({ ...campaignForm, templateId: e.target.value || undefined })}
              >
                <MenuItem value="">
                  <em>Use custom message instead</em>
                </MenuItem>
                {templates
                  .filter(t => t.isActive && (t.templateType === campaignForm.campaignType || t.templateType === 'both'))
                  .map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} ({template.category})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {!campaignForm.templateId && (
              <Alert severity="info">
                <Typography variant="caption">
                  No template selected. You can provide a custom message below or select a template above.
                </Typography>
              </Alert>
            )}

            <Divider><Typography variant="caption">Target Audience</Typography></Divider>

            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select
                value={campaignForm.targetAudience}
                label="Target Audience"
                onChange={(e) => setCampaignForm({ ...campaignForm, targetAudience: e.target.value as TargetAudience })}
              >
                <MenuItem value="all_users">All Users</MenuItem>
                <MenuItem value="all_renters">All Renters</MenuItem>
                <MenuItem value="all_hosts">All Hosts</MenuItem>
                <MenuItem value="all_drivers">All Drivers</MenuItem>
                <MenuItem value="user_segment">User Segment (Filter)</MenuItem>
                <MenuItem value="specific_users">Specific Users</MenuItem>
              </Select>
            </FormControl>

            {campaignForm.targetAudience === 'user_segment' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2">Segment Filters</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="City"
                      fullWidth
                      placeholder="e.g., Lagos"
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        segmentFilters: { ...campaignForm.segmentFilters, city: e.target.value }
                      })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      fullWidth
                      placeholder="e.g., active"
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        segmentFilters: { ...campaignForm.segmentFilters, status: e.target.value }
                      })}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {campaignForm.targetAudience === 'specific_users' && (
              <Box>
                <TextField
                  label="Phone Numbers (one per line)"
                  fullWidth
                  multiline
                  rows={4}
                  value={targetPhones}
                  placeholder="+2348012345678&#10;+2347098765432&#10;+2348123456789"
                  helperText="Enter phone numbers in international format (e.g., +234...)"
                  onChange={(e) => setTargetPhones(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email Addresses (one per line)"
                  fullWidth
                  multiline
                  rows={4}
                  value={targetEmails}
                  placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                  helperText="Or enter email addresses (one per line)"
                  onChange={(e) => setTargetEmails(e.target.value)}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    You can enter phone numbers, email addresses, or both. Backend will match users by phone/email.
                  </Typography>
                </Alert>
              </Box>
            )}

            {campaignForm.templateId && templates.find(t => t.id === campaignForm.templateId) && (
              <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Template Variables
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Provide values for template variables (same value will be used for all recipients):
                </Typography>
                {templates.find(t => t.id === campaignForm.templateId)?.variables.map((variable) => (
                  <TextField
                    key={variable}
                    label={variable}
                    fullWidth
                    size="small"
                    sx={{ mt: 1 }}
                    placeholder={`Value for {{${variable}}}`}
                    onChange={(e) => setCampaignForm({
                      ...campaignForm,
                      templateVariables: {
                        ...campaignForm.templateVariables,
                        [variable]: e.target.value
                      }
                    })}
                  />
                ))}
              </Box>
            )}

            <Alert severity="warning">
              <Typography variant="caption">
                Campaign will be sent immediately after creation. Double-check your target audience and message.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCampaignDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSendCampaign} variant="contained" disabled={submitting || !campaignForm.name} startIcon={<Send />}>
            {submitting ? <CircularProgress size={20} /> : 'Send Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Stats Dialog - Placeholder */}
      <Dialog
        open={campaignStatsDialog.open}
        onClose={() => setCampaignStatsDialog({ open: false, campaignId: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Campaign Analytics</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Campaign analytics dashboard will load here with detailed stats and recipient status...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignStatsDialog({ open: false, campaignId: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastMessagingPage;
